<template>
  <div v-if="totalCartAmount > 0" ref="container" class="transfers-store-credit">
    <img
      class="transfers-store-credit__icon"
      alt="Loyalty Badge"
      src="https://jiffy.imgix.net/gen/loyalty-badge.png"
    />
    <div class="transfers-store-credit__content">
      <div class="transfers-store-credit__text">
        <span v-if="currentExpectedCredits > 0">
          You'll earn <strong>{{ n(currentExpectedCredits, "currency") }}</strong> credit on
          Transfers!
        </span>
        <span v-else>Earn credits on Transfers!</span>
        <Tooltip trigger="click" position="bottom-right" @show="toggleTooltip">
          <template #target>
            <UseSvg icon="info-circle-bold" class="transfers-store-credit__info-icon" />
          </template>
          <template #content>
            <StoreCreditTiers
              :current-tier="currentTier"
              :tiers="tiers"
              :total-amount="totalCartAmount"
              is-tooltip
            />
          </template>
        </Tooltip>
      </div>
      <div v-if="creditAmount > 0" class="transfers-store-credit__store-credit">
        <span class="transfers-store-credit__store-credit-label">Transfers Credit Balance:</span>
        <span class="transfers-store-credit__store-credit-amount">
          {{ n(creditAmount, "currency") }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { watch } from "vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { n } from "components/jiffy/vue-cart/helpers/currency";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import StoreCreditTiers from "common/vue/components/StoreCreditTiers.vue";
import Tooltip from "common/vue/components/Tooltip.vue";
import useTransfersStoreCredit from "common/vue/composables/useTransfersStoreCredit";

export default {
  name: "TransfersStoreCredit",
  components: {
    StoreCreditTiers,
    UseSvg,
    Tooltip
  },
  props: {
    storeCreditInfo: {
      type: Object,
      required: true,
      validator: value =>
        ["userStoreCredits", "expectedCredits", "orderSubtotal"].every(key => key in value)
    }
  },
  // eslint-disable-next-line max-lines-per-function
  setup(props) {
    const {
      creditAmount,
      currentExpectedCredits,
      tiers,
      currentTier,
      totalCartAmount,
      orderSubtotal,
      expectedCredits,
      updateStoreCreditInfo
    } = useTransfersStoreCredit();

    watch(
      () => props.storeCreditInfo,
      newValue => {
        updateStoreCreditInfo(newValue);
      },
      { immediate: true }
    );

    const toggleTooltip = () => {
      submitDtfEvent("transfer_store_credit_info_icon_clicked", {
        credits: currentExpectedCredits.value,
        order_subtotal: orderSubtotal.value,
        expected_credits: expectedCredits.value,
        current_tier: currentTier.value?.range,
        credit_amount: creditAmount.value
      });
    };

    return {
      toggleTooltip,
      creditAmount,
      currentExpectedCredits,
      tiers,
      currentTier,
      totalCartAmount,
      n
    };
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.transfers-store-credit {
  $this: &;

  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: $light-aqua;
  border-radius: 4px;
  width: 100%;

  &__icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    flex-wrap: wrap;
    gap: 3px;
  }

  &__text {
    font-size: 14px;
    line-height: 16px;
    color: $crafterpay-gray-modern-800;
    font-weight: 400;
    text-align: left;
    position: relative;
    display: flex;
    align-items: center;
    gap: 3px;

    :deep(.vue-tooltip__content) {
      right: -42px;
    }
  }

  &__info-icon {
    height: 16px;
    width: 16px;
    color: $crafterpay-gray-modern-800;
    cursor: pointer;
    margin-left: 3px;
  }

  &__store-credit {
    display: flex;
    gap: 5px;
  }

  &__store-credit-label {
    font-size: 13px;
    color: $crafterpay-gray-modern-800;
    font-weight: 700;
    line-height: 13px;
  }

  &__store-credit-amount {
    font-size: 15px;
    font-weight: 700;
    line-height: 15px;
    color: $green-emerald-small-light;
  }
}
</style>
